import type { ReactNode } from 'react';

/**
 * Support is the one static informational page left in the app; Privacy Policy
 * and Terms of Use live at aslgo.com/legal and are linked there from both the
 * footer and the registration consent steps. The copy below is best-guess
 * DRAFT text — it should be reviewed and replaced with approved support
 * wording before launch.
 */

interface InfoPageProps {
  title: string;
  subtitle?: string;
  onBack: () => void;
  children: ReactNode;
}

function InfoPage({ title, subtitle, onBack, children }: InfoPageProps) {
  return (
    <div className="card p-7 lg:p-10 has-grain animate-fade-up">
      <button
        type="button"
        onClick={onBack}
        className="text-[0.85rem] text-ink-600 dark:text-ink-400 hover:underline"
      >
        ← Back
      </button>

      <h2 className="h-section mt-4 mb-1 text-ink-900 dark:text-ink-100">{title}</h2>
      {subtitle ? <p className="hint mb-2">{subtitle}</p> : null}

      <div className="mt-5 mb-7 text-[0.78rem] leading-relaxed
                      rounded-lg px-3 py-2
                      text-amber-800 dark:text-amber-200
                      bg-amber-50 dark:bg-amber-950/40
                      border border-amber-200 dark:border-amber-900/60">
        Draft placeholder copy — review and replace with approved wording before launch.
      </div>

      <div className="space-y-7">{children}</div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h3 className="h-card mb-2 text-ink-900 dark:text-ink-100">{title}</h3>
      <div className="space-y-3 text-[0.95rem] leading-relaxed text-ink-700 dark:text-ink-300">
        {children}
      </div>
    </section>
  );
}

function Bullets({ items }: { items: ReactNode[] }) {
  return (
    <ul className="list-disc pl-5 space-y-1.5 text-[0.95rem] leading-relaxed text-ink-700 dark:text-ink-300">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  );
}

/** Rendered as a mailto: link on the Support page. */
const SUPPORT_EMAIL = 'hello@aslgo.com';

export function SupportView({ onBack }: { onBack: () => void }) {
  return (
    <InfoPage
      title="Support"
      subtitle="We're here to help you get the most out of ASL Go."
      onBack={onBack}
    >
      <Section title="Contact us">
        <p>
          Email us at{' '}
          <a className="font-medium underline underline-offset-2" href={`mailto:${SUPPORT_EMAIL}`}>
            {SUPPORT_EMAIL}
          </a>{' '}
          and we'll get back to you, usually within one to two business days. Please
          include your account email and a short description of what you ran into.
        </p>
      </Section>

      <Section title="Before you reach out">
        <p>A few quick things solve most issues:</p>
        <Bullets
          items={[
            'Refresh the page, or sign out and back in.',
            'Make sure your browser is allowed to use your camera and microphone.',
            'Check your internet connection — video calls work best on a stable network.',
            "For the most reliable experience, use Chrome on a laptop or desktop — screen sharing and call notifications aren't available in every browser.",
          ]}
        />
      </Section>

      <Section title="Common topics">
        <Bullets
          items={[
            'Account and sign-in help',
            'Camera, microphone, or video-quality problems',
            'Minutes, billing, and payments',
            'Becoming a guide and getting approved',
            'Live captions during a call',
          ]}
        />
      </Section>
    </InfoPage>
  );
}
